/**
 * useIsChatStreaming — is THIS chat streaming right now?
 *
 * The sidebar's per-row subscription, and the only thing that makes cross-chat
 * concurrent streaming visible.
 */

import { useCallback, useSyncExternalStore } from 'react';
import {
  peekSharedRuntime,
  resolveSessionKey
} from 'src/content/ChatV2/chatRuntime';

const NO_STREAMS: ReadonlySet<string> = new Set();
const NEVER_CHANGES = () => () => {};

/**
 * `string | number`, not `ChatId`: the sidebar holds NUMERIC backend ids
 * (`ChatHistoryItem.id`), so both are accepted here.
 */
export function useIsChatStreaming(
  chatId: string | number | undefined
): boolean {
  const registry = peekSharedRuntime()?.machineRegistry;

  const subscribe = useCallback(
    (onStoreChange: () => void) =>
      registry ? registry.subscribeToStreaming(onStoreChange) : NEVER_CHANGES(),
    [registry]
  );
  const getSnapshot = useCallback(
    () => (registry ? registry.getStreamingChats() : NO_STREAMS),
    [registry]
  );

  const streaming = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);

  if (chatId === undefined) {
    return false;
  }
  return streaming.has(String(resolveSessionKey(chatId) ?? chatId));
}
